/**
 * Upload policy shared by client (early feedback) and server (enforcement).
 * The server never trusts the declared type: it sniffs file signatures.
 */

const MB = 1024 * 1024;

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'] as const;

export const OOXML_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
] as const;

export const DOCUMENT_TYPES = [...IMAGE_TYPES, 'application/pdf', 'text/plain', 'text/csv', ...OOXML_TYPES] as const;

export type UploadKind = 'image' | 'document';

export const UPLOAD_LIMITS: Record<UploadKind, { types: readonly string[]; maxBytes: number }> = {
  image: { types: IMAGE_TYPES, maxBytes: 25 * MB },
  document: { types: DOCUMENT_TYPES, maxBytes: 50 * MB },
};

/** Thumbnails are generated on the uploading device and re-validated by the server. */
export const THUMBNAIL_TYPES = ['image/jpeg', 'image/webp', 'image/png'] as const;
export const THUMBNAIL_MAX_BYTES = 2 * MB;
export const THUMBNAIL_MAX_EDGE = 640;

/** Which kind of upload each file-owning collection accepts. */
export const COLLECTION_UPLOAD_KIND: Readonly<Record<string, UploadKind>> = {
  photos: 'image',
  pets: 'image',
  memories: 'image',
  documents: 'document',
};

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * MB) return `${(bytes / MB).toFixed(1)} MB`;
  return `${(bytes / (1024 * MB)).toFixed(2)} GB`;
}
